import React, { useEffect } from "react";
import { Form, Input, Select, DatePicker, Row, Col, Typography, Tag, Tooltip, Divider } from "antd";
import { InfoCircleOutlined, ClockCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { UTCDatePicker, UTCTimePicker } from "../DateTimeInput";
import { useTimeZone } from "../../contexts/TimeZoneContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import AircraftSelector from "./AircraftSelector";

dayjs.extend(utc);
dayjs.extend(duration);

const { Text } = Typography;

// 从 FlightInfoEditor.jsx 移植过来的辅助函数和组件
const calculateTimeDifference = (startTime, endTime) => {
    if (!startTime || !endTime || !startTime.isValid() || !endTime.isValid()) {
        return null;
    }
    const start = startTime.utc();
    const end = endTime.utc();
    const diffMs = end.diff(start);
    if (diffMs < 0) {
        return null;
    }
    const diffDuration = dayjs.duration(diffMs);
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const formattedDiff = `${hours}h ${minutes}m`;
    return {
        formatted: formattedDiff,
        minutes: totalMinutes,
    };
};

const TimeDifferenceDisplay = ({ startTime, endTime, label, type = "default" }) => {
    const diff = calculateTimeDifference(startTime, endTime);
    if (!diff) {
        return null;
    }
    let color = "blue";
    switch (type) {
        case "plan":
            color = "geekblue";
            break;
        case "estimate":
            color = "cyan";
            break;
        case "actual":
            color = "green";
            break;
        case "airborne":
            color = "purple";
            break;
        default:
            color = "blue";
    }
    return (
        <Tooltip title={`${label}: ${diff.minutes} 分钟`}>
            <Tag icon={<ClockCircleOutlined />} color={color} style={{ marginLeft: "8px", cursor: "default", fontSize: "12px" }}>
                {diff.formatted}
            </Tag>
        </Tooltip>
    );
};

/**
 * 航班信息表单组件
 * 支持普通编辑模式和对比展示模式
 * V2 - 引入时间差显示和类编辑器布局
 */
const FlightInfoForm = ({
    form,
    currentValues = {},
    suggestedValues = {},
    onValuesChange,
    fieldsToDisplay = [], //确保有默认值
    fieldConfig = {
        displayMode: "editOnly",
        highlightChanged: true,
    },
}) => {
    const { timeZone } = useTimeZone();
    const isCompareMode = fieldConfig.displayMode === "compare";

    // 使用 Form.useWatch 监听表单中的值，用于时间差计算
    const formValues = Form.useWatch([], form);

    const isFieldChanged = (fieldName) => {
        if (!fieldConfig.highlightChanged || !currentValues || !suggestedValues) return false;
        if (["std", "sta", "etd", "eta", "atd", "ata", "atd_icao", "ata_icao"].includes(fieldName)) {
            const currentTime = currentValues[fieldName] ? dayjs.utc(currentValues[fieldName]) : null;
            const suggestedTime = suggestedValues[fieldName] ? dayjs.utc(suggestedValues[fieldName]) : null;
            if ((!currentTime && suggestedTime) || (currentTime && !suggestedTime)) return true;
            if (currentTime && suggestedTime && !currentTime.isSame(suggestedTime)) return true;
            return false;
        }
        return currentValues[fieldName] !== suggestedValues[fieldName];
    };

    const renderComparisonValue = (value, fieldName, type = "text") => {
        let displayValue = "无";
        if (type === "time" && value) {
            displayValue = dayjs.utc(value).format("YYYY-MM-DD HH:mm");
        } else if (value) {
            displayValue = value;
        }
        return displayValue;
    };

    const renderFieldWithComparison = (fieldName, label, fieldType = "time", required = false, customInput) => {
        const changed = isFieldChanged(fieldName);
        // 新增判断: 建议值存在且不为空，但与当前值相同
        const hasMatchingValues = !changed && suggestedValues && suggestedValues[fieldName] !== undefined && suggestedValues[fieldName] !== null;

        const baseLabel = (
            <div style={{ display: "flex", alignItems: "center" }}>
                {label}
                {fieldType === "sta" && <TimeDifferenceDisplay startTime={formValues?.std} endTime={formValues?.sta} label="计划飞行时间" type="plan" />}
                {fieldType === "eta" && <TimeDifferenceDisplay startTime={formValues?.etd} endTime={formValues?.eta} label="预计飞行时间" type="estimate" />}
                {fieldType === "ata" && <TimeDifferenceDisplay startTime={formValues?.atd} endTime={formValues?.ata} label="实际飞行时间" type="actual" />}
                {fieldType === "ata_icao" && <TimeDifferenceDisplay startTime={formValues?.atd_icao} endTime={formValues?.ata_icao} label="空中飞行时间" type="airborne" />}
            </div>
        );

        return (
            <Form.Item label={baseLabel} required={required} style={{ flex: 1, marginBottom: "12px" }}>
                {isCompareMode && (
                    <div style={{ marginBottom: suggestedValues[fieldName] ? "8px" : "0" }}>
                        <Row gutter={8}>
                            <Col span={10}>
                                <Text strong>当前:</Text> {renderComparisonValue(currentValues[fieldName], fieldName, fieldType === "text" ? "text" : "time")}
                            </Col>
                            <Col span={14}>
                                <Text strong style={{ color: changed ? "#faad14" : undefined }}>
                                    建议:
                                </Text>
                                <span style={{ color: changed ? "#faad14" : undefined, fontWeight: changed ? "bold" : "normal" }}>{renderComparisonValue(suggestedValues[fieldName], fieldName, fieldType === "text" ? "text" : "time")}</span>
                                {changed && <InfoCircleOutlined style={{ marginLeft: 4, color: "#faad14" }} />}
                                {hasMatchingValues && <CheckCircleOutlined style={{ marginLeft: 4, color: "#52c41a" }} title="建议值与当前值一致" />}
                            </Col>
                        </Row>
                    </div>
                )}
                <Form.Item name={fieldName} noStyle rules={[{ required: required, message: `请输入${label}` }]}>
                    {customInput || <UTCDatePicker showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm" style={{ width: "100%" }} />}
                </Form.Item>
            </Form.Item>
        );
    };

    const shouldDisplayField = (fieldName) => fieldsToDisplay.includes(fieldName);

    return (
        <Form form={form} layout="vertical" onValuesChange={onValuesChange} initialValues={suggestedValues}>
            <Row gutter={16}>
                {shouldDisplayField("flight_number") && (
                    <Col xs={24} sm={12} md={8}>
                        {renderFieldWithComparison("flight_number", "航班号", "text", true, <Input disabled />)}
                    </Col>
                )}
                {shouldDisplayField("departure_airport") && (
                    <Col xs={24} sm={12} md={8}>
                        {renderFieldWithComparison("departure_airport", "出发机场", "text", true, <Input disabled />)}
                    </Col>
                )}
                {shouldDisplayField("arrival_airport") && (
                    <Col xs={24} sm={12} md={8}>
                        {renderFieldWithComparison("arrival_airport", "到达机场", "text", true, <Input disabled />)}
                    </Col>
                )}
            </Row>

            {/* 时间组 */}
            <Row gutter={16}>
                {shouldDisplayField("std") && (
                    <Col xs={24} sm={12}>
                        {renderFieldWithComparison("std", "计划起飞 (STD)", "time", true)}
                    </Col>
                )}
                {shouldDisplayField("sta") && (
                    <Col xs={24} sm={12}>
                        {renderFieldWithComparison("sta", "计划到达 (STA)", "sta", true)}
                    </Col>
                )}
            </Row>

            <Row gutter={16}>
                {shouldDisplayField("etd") && (
                    <Col xs={24} sm={12}>
                        {renderFieldWithComparison("etd", "预计起飞 (ETD)", "time")}
                    </Col>
                )}
                {shouldDisplayField("eta") && (
                    <Col xs={24} sm={12}>
                        {renderFieldWithComparison("eta", "预计到达 (ETA)", "eta")}
                    </Col>
                )}
            </Row>

            <Row gutter={16}>
                {shouldDisplayField("atd") && (
                    <Col xs={24} sm={12}>
                        {renderFieldWithComparison("atd", "实际撤轮挡 (Off-block)", "time")}
                    </Col>
                )}
                {shouldDisplayField("ata") && (
                    <Col xs={24} sm={12}>
                        {renderFieldWithComparison("ata", "实际上轮档 (On-block)", "ata")}
                    </Col>
                )}
            </Row>

            <Row gutter={16}>
                {shouldDisplayField("atd_icao") && (
                    <Col xs={24} sm={12}>
                        {renderFieldWithComparison("atd_icao", "实际离地 (Airborne)", "time")}
                    </Col>
                )}
                {shouldDisplayField("ata_icao") && (
                    <Col xs={24} sm={12}>
                        {renderFieldWithComparison("ata_icao", "实际降落 (Touch-down)", "ata_icao")}
                    </Col>
                )}
            </Row>

            <Row gutter={16}>
                {shouldDisplayField("aircraft_reg") && (
                    <Col xs={24} sm={12} md={8}>
                        {renderFieldWithComparison("aircraft_reg", "飞机注册号", "text", false, <AircraftSelector allowClear />)}
                    </Col>
                )}
                {shouldDisplayField("delay_code") && (
                    <Col xs={24} sm={12} md={8}>
                        {renderFieldWithComparison(
                            "delay_code",
                            "延误代码",
                            "text",
                            false,
                            <Select placeholder="选择延误代码" allowClear>
                                <Select.Option value="WA">WA - 天气</Select.Option>
                                <Select.Option value="TC">TC - 交通管制</Select.Option>
                                {/* ... 其他选项 ... */}
                            </Select>,
                        )}
                    </Col>
                )}
                {shouldDisplayField("delay_minutes") && (
                    <Col xs={24} sm={12} md={8}>
                        {renderFieldWithComparison("delay_minutes", "延误分钟", "text", false, <Input type="number" min={0} />)}
                    </Col>
                )}
            </Row>
        </Form>
    );
};

export default FlightInfoForm;
